import Component from "../../lib/component";
import Tracker from "../../lib/tracker";
import {getTrackerOptions} from "../../lib/tracker_helpers";

export const AnalyticsWatcherConfig = {
    componentTypeKey: 'AnalyticsWatcher',
    baseHtmlClass: 'js--analytics_watcher',
    selector: '.js--analytics_watcher',

    initedHtmlClassModifier: 'inited',
    viewedHtmlClassModifier: 'viewed',

    datasetConfigKey: 'analytics_watcher__config',
    trackerOptionsDatasetConfigKey: 'tracker_options',
    analyticsEventNameDatasetConfigKey: 'event_name',
    analyticsParametersDatasetConfigKey: 'analytics_parameters',
    intersectionThresholdDatasetConfigKey: 'intersection_threshold',

    defaultIntersectionThreshold: 0,
};

/**
 * The AnalyticsWatcher component watches elements for when they are "viewed" and sends an event to a Tracker. The Tracker will handle the event appropriately for the environment (ie. native app, web).
 */
export class AnalyticsWatcher extends Component {

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: AnalyticsWatcherConfig
        });

        this.setDatasetConfig(AnalyticsWatcherConfig.datasetConfigKey);
        this.eventName = this.getAnalyticsEventName();
        this.analyticsParameters = this.getAnalyticsParameters();
        this.intersectionThreshold = this.getIntersectionThreshold();
        this.viewed = false;

        this.trackerOptions = getTrackerOptions(this.getDatasetConfigValue(AnalyticsWatcherConfig.trackerOptionsDatasetConfigKey));

        this.tracker = new Tracker(window, document, this.trackerOptions);

        // Set up observer to handle capturing stats impressions (views) of the component within it's parent element.
        this.viewedObserver = null;
        this.initViewedObserver();
        this.observeViewed();

        this.render();
        this.element.classList.add(this.htmlClassNameForState(AnalyticsWatcherConfig.initedHtmlClassModifier));
    }

    render() {
        this.conditionallySetHtmlClassState(this.isViewed(), AnalyticsWatcherConfig.viewedHtmlClassModifier);
    }

    /**
     * Set up an intersection observer for when the element is viewed within its scrollable parent element.
     * @return {undefined}
     */
    initViewedObserver() {
        if (!IntersectionObserver) return;
        let options = {
            root: this.getParentScrollElement(),
            rootMargin: '10px',
            threshold: this.intersectionThreshold,
        };
        this.viewedObserver = new IntersectionObserver(this.handleViewed.bind(this), options);
    }

    /**
     * Returns the component's parent element that is used for detecting if the component is visible.
     *
     * @return {Node|undefined} The DOM element that the component's element scrolls within.
     */
    getParentScrollElement() {
        return undefined; // This will mean the window is used as the scroll element.
    }

    /**
     * Tell the viewed intersection observer to start observing.
     * @return {undefined}
     */
    observeViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.observe(this.element);
    }

    /**
     * Tell the viewed intersection observer to stop observing.
     * @return {undefined}
     */
    unobserveViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.unobserve(this.element);
    }

    /**
     * Callback for when the viewedObserver detects the component intersecting the parent scroll element.
     * @param entries {IntersectionObserverEntry}
     * @param observer {IntersectionObserver}
     */
    handleViewed(entries, observer) {
        entries.forEach((entry) => {
            if (entry && entry.isIntersecting) {
                this.trackViewEvent();
            }
        });
        this.render();
    }

    trackViewEvent() {
        this.viewed = true;
        if (!this.eventName) console.warn(`[AnalyticsWatcher] trackViewEvent() eventName is blank!`, this.eventName);
        if (!this.tracker || !this.tracker.eventTracker) return;
        this.tracker.eventTracker(this.eventName, this.analyticsParameters);
    }

    isViewed() {
        return !!this.viewed;
    }

    getAnalyticsParameters() {
        const parameters = this.getDatasetConfigValue(AnalyticsWatcherConfig.analyticsParametersDatasetConfigKey);
        if (!parameters) return;
        return parameters;
    }

    getAnalyticsEventName() {
        const eventName = this.getDatasetConfigValue(AnalyticsWatcherConfig.analyticsEventNameDatasetConfigKey);
        if (!eventName) return;
        return eventName;
    }

    getIntersectionThreshold() {
        const threshold = this.getDatasetConfigValue(AnalyticsWatcherConfig.intersectionThresholdDatasetConfigKey);
        if (!threshold) return AnalyticsWatcherConfig.defaultIntersectionThreshold;
        return threshold;
    }

}
